/// <amd-module name="Feed/Connect/Components/ToolDetails" />
import * as React from "react";
import { connect } from "react-redux";

import { ScreenBreakpoints } from "VSS/Core/Util/Screen";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { Uri } from "VSS/Platform/Util/Url";

import { Button } from "VSSUI/Button";
import { Link } from "VSSUI/Link";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Tab, TabBar, TabSize } from "VSSUI/Tabs";

import * as Res from "Feed/Connect/Resources";
import { PackagingService, TfsService } from "Feed/Core/PackagingService";
import { getPackagingService, getTfsService, isMember } from "Feed/Core/Redux/FeedHub";
import { IUxFeedView } from "Feed/Core/UxFeedView";

import { NpmTool } from "../Protocols/npm";
import {
    ConnectToFeedActions,
    getIsHosted,
    getOrganizationName,
    getSelectedTool,
    getSelectedUxFeedView,
    IConnectToFeedAwareState
} from "../Redux/ConnectToFeed";
import { ITool, ToolId } from "../Tool";
import { CopyableCode, DetailsCard, FWLink, PublishDetailsCard, ToolIcon, WidthBreakpoint } from "./Common";
import { FirstTimeMessageText } from "./FirstTimeMessage";

const Actions = {
    getTools: ConnectToFeedActions.getTools
};

enum AuthType {
    PAT = "PAT",
    AAD = "AAD",
    MSA = "MSA"
}

export enum OperatingSystem {
    Windows = "Windows",
    Other = "allOS"
}

interface IToolDetailsOwnProps {
    collectionName: string;
    feed: IUxFeedView;
    packagingService: PackagingService;
    tfsService: TfsService;
    tool: ITool;
    isHosted: boolean;
    userIsMember: boolean;
}

type IToolDetailsProps = IToolDetailsOwnProps & typeof Actions & { onBackButtonClick: () => void };

const ToolDetailsComponents: { [toolId: number]: (props: IToolDetailsProps) => JSX.Element } = {};

function getToolDetailsComponent(props: IToolDetailsProps) {
    const ToolDetailsComponent = ToolDetailsComponents[props.tool.id];
    return ToolDetailsComponent ? <ToolDetailsComponent {...props} /> : null;
}

class UnconnectedToolDetails extends React.PureComponent<IToolDetailsProps> {
    public render() {
        return (
            <div className="tool-details flex-grow scroll-auto">
                {this.getOverview()}
                <div className="rhythm-vertical-16">
                    <FirstTimeMessageText tool={this.props.tool} className="firstTimeMessage flex-self-start" />
                    {getToolDetailsComponent(this.props)}
                </div>
            </div>
        );
    }

    private getOverview() {
        const { tool, getTools, onBackButtonClick } = this.props;
        return (
            <div className="overview">
                <div className="flex-row flex-center">
                    <WidthBreakpoint widthLessThan={ScreenBreakpoints.medium}>
                        {onBackButtonClick && (
                            <Button
                                ariaLabel={"Back"}
                                className={"bolt-header-back-button justify-end"}
                                iconProps={{
                                    iconName: "Back",
                                    size: undefined
                                }}
                                subtle={true}
                                onClick={onBackButtonClick}
                            />
                        )}
                    </WidthBreakpoint>

                    <ToolIcon tool={tool} className="icon-large-margin" />
                    <h2 className="title-m flex-grow">{tool.name}</h2>
                    <Button text={Res.GetTools_Text} primary={true} onClick={() => getTools(tool)} />
                </div>

                {tool.getToolLink && (
                    <div className="flex-row">
                        <span className="icon-large-margin" />
                        <Link rel="nofollow noreferrer noopener help" target="_blank" {...tool.getToolLink()} />
                    </div>
                )}
            </div>
        );
    }
}

function mapStateToProps(state: IConnectToFeedAwareState): IToolDetailsOwnProps {
    return {
        collectionName: getOrganizationName(state) || "",
        feed: getSelectedUxFeedView(state)!,
        packagingService: getPackagingService(state),
        tfsService: getTfsService(state),
        tool: getSelectedTool(state)!,
        isHosted: getIsHosted(state) || false,
        userIsMember: isMember(state)
    };
}

export const ToolDetails = connect(mapStateToProps, Actions)(UnconnectedToolDetails);

function nugetConfig(source: string, feedIndexUrl: string) {
    return (
        <React.Fragment>
            <pre className="secondary-text">{`<?xml version="1.0" encoding="utf-8"?>
<configuration>
  <packageSources>
    <clear />`}</pre>
            <pre>{`    <add key="${source}" value="${feedIndexUrl}" />`}</pre>
            <pre className="secondary-text">{`  </packageSources>
</configuration>`}</pre>
        </React.Fragment>
    );
}

function nugetPackageSource(source: string, feedIndexUrl: string) {
    return `<add key="${source}" value="${feedIndexUrl}" />`;
}

function getNuGetProjectSetUp(source: string, url: string, format: string): JSX.Element {
    return (
        <React.Fragment>
            <p className={format}>
                <FormatComponent format={Res.ProjectSetup_Nuget_Details}>
                    <code>nuget.config</code>
                    <code>.csproj</code>
                    <code>.sln</code>
                </FormatComponent>
            </p>
            <CopyableCode text={nugetPackageSource(source, url)} ariaLabel={Res.ProjectSetup}>
                {nugetConfig(source, url)}
            </CopyableCode>
        </React.Fragment>
    );
}

ToolDetailsComponents[ToolId.DotNet] = ({ tool, packagingService, feed, userIsMember, isHosted, tfsService }) => {
    const [selectedTabId, setSelectedTabId] = React.useState<string>(OperatingSystem.Windows);
    const source = feed.name;
    const url = tool.getFeedUrl(packagingService, feed);

    if (userIsMember) {
        return (
            <React.Fragment>
                <DetailsCard title={{ text: Res.ProjectSetup, ariaLevel: 3 }}>
                    {isHosted ? (
                        <React.Fragment>
                            <FormatComponent format={Res.ProjectSetup_CredProvider}>
                                <b>{Res.CredentialProvider}</b>
                                <b>{Res.CredentialProvider}</b>
                            </FormatComponent>
                            {getNuGetProjectSetUp(source, url, "")}
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            <TabBar
                                onSelectedTabChanged={setSelectedTabId}
                                selectedTabId={selectedTabId}
                                tabSize={TabSize.Compact}
                            >
                                <Tab
                                    name="Windows"
                                    id={OperatingSystem.Windows}
                                    iconProps={{ iconName: "WindowsLogo" }}
                                />
                                <Tab name={Res.Other} id={OperatingSystem.Other} iconProps={{ iconName: "PC1" }} />
                            </TabBar>
                            {getNuGetProjectSetUp(source, url, "tooldetails-smaller-margin")}
                        </React.Fragment>
                    )}

                    {selectedTabId === OperatingSystem.Windows && !isHosted && (
                        <React.Fragment>
                            <p className="body-s">
                                <FormatComponent format={Res.PAT_Issues}>
                                    <Link className="body-s" onClick={() => setSelectedTabId(OperatingSystem.Other)}>
                                        {Res.PAT_Instructions}
                                    </Link>
                                </FormatComponent>
                            </p>
                        </React.Fragment>
                    )}

                    {selectedTabId === OperatingSystem.Other && (
                        <React.Fragment>
                            <div className="title-s margin-top-16">{Res.SetupCredentials}</div>
                            <div className="rhythm-vertical-20 card-steps margin-top-8">
                                <div>
                                    <div className="title-xs">{Res.Step} 1</div>
                                    <p>
                                        <FormatComponent format={Res.GeneratePAT}>
                                            <Link href={tfsService.getSessionTokenUri()} target="_blank">
                                                {Res.PersonalAccessToken}
                                            </Link>
                                            <em>{Res.Packaging}</em>
                                        </FormatComponent>
                                    </p>
                                </div>

                                <div>
                                    <div className="title-xs">{Res.Step} 2</div>
                                    <p>
                                        <FormatComponent format={Res.PastePATAndUserName_DotNet_Nuget}>
                                            <code>[{Res.PersonalAccessToken}]</code>
                                        </FormatComponent>
                                    </p>
                                    <CopyableCode ariaLabel={Res.UpdateSource}>
                                        dotnet nuget update source {source} --valid-authentication-types basic
                                        --username "{source}" --password [{Res.PersonalAccessToken}]
                                    </CopyableCode>
                                </div>
                            </div>
                        </React.Fragment>
                    )}
                </DetailsCard>

                <DetailsCard title={{ text: Res.RestorePackages, ariaLevel: 3 }}>
                    <p>
                        <FormatComponent format={Res.RestorePackages_Dotnet_Details}>
                            <code>dotnet</code>
                        </FormatComponent>
                    </p>
                    <CopyableCode ariaLabel={Res.RestorePackages}>dotnet restore --interactive</CopyableCode>
                    <p className="body-s">
                        <FormatComponent format={Res.RestorePackages_Dotnet_Note}>
                            <code>--interactive</code>
                            <code>dotnet</code>
                            <code>--interactive</code>
                        </FormatComponent>
                    </p>
                </DetailsCard>

                <PublishDetailsCard feed={feed}>
                    <p>{Res.PublishDetails_Nuget}</p>
                    <CopyableCode ariaLabel={Res.PublishPackages}>
                        dotnet nuget push --source "{source}" --api-key az &lt;package-path&gt;
                    </CopyableCode>
                </PublishDetailsCard>
            </React.Fragment>
        );
    } else {
        return (
            <React.Fragment>
                <DetailsCard title={{ text: Res.ProjectSetup, ariaLevel: 3 }}>
                    <p>{Res.ProjectSetup_Nuget_Details}</p>
                    <CopyableCode text={nugetPackageSource(source, url)} ariaLabel={Res.ProjectSetup}>
                        {nugetConfig(source, url)}
                    </CopyableCode>
                </DetailsCard>

                <DetailsCard title={{ text: Res.RestorePackages, ariaLevel: 3 }}>
                    <p>{Res.RestorePackages}</p>
                    <CopyableCode ariaLabel={Res.RestorePackages}>dotnet restore</CopyableCode>
                </DetailsCard>
            </React.Fragment>
        );
    }
};

ToolDetailsComponents[ToolId.NuGet] = ({ tool, packagingService, feed, userIsMember, isHosted, tfsService }) => {
    const source = feed.name;
    const url = tool.getFeedUrl(packagingService, feed);
    const [selectedTabId, setSelectedTabId] = React.useState<string>(OperatingSystem.Windows);

    if (userIsMember) {
        return (
            <React.Fragment>
                <DetailsCard title={{ text: Res.ProjectSetup, ariaLevel: 3 }}>
                    {isHosted ? (
                        <React.Fragment>
                            <FormatComponent format={Res.ProjectSetup_CredProvider}>
                                <b>{Res.CredentialProvider}</b>
                                <b>{Res.CredentialProvider}</b>
                            </FormatComponent>
                            {getNuGetProjectSetUp(source, url, "")}
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            <TabBar
                                onSelectedTabChanged={setSelectedTabId}
                                selectedTabId={selectedTabId}
                                tabSize={TabSize.Compact}
                            >
                                <Tab
                                    name="Windows"
                                    id={OperatingSystem.Windows}
                                    iconProps={{ iconName: "WindowsLogo" }}
                                />
                                <Tab name={Res.Other} id={OperatingSystem.Other} iconProps={{ iconName: "PC1" }} />
                            </TabBar>
                            {getNuGetProjectSetUp(source, url, "tooldetails-smaller-margin")}
                        </React.Fragment>
                    )}

                    {selectedTabId === OperatingSystem.Windows && !isHosted && (
                        <React.Fragment>
                            <p className="body-s">
                                <FormatComponent format={Res.PAT_Issues}>
                                    <Link className="body-s" onClick={() => setSelectedTabId(OperatingSystem.Other)}>
                                        {Res.PAT_Instructions}
                                    </Link>
                                </FormatComponent>
                            </p>
                        </React.Fragment>
                    )}

                    {selectedTabId === OperatingSystem.Other && (
                        <React.Fragment>
                            <div className="title-s margin-top-16">{Res.SetupCredentials}</div>
                            <div className="rhythm-vertical-20 card-steps margin-top-8">
                                <div>
                                    <div className="title-xs">{Res.Step} 1</div>
                                    <FormatComponent format={Res.GeneratePAT}>
                                        <Link href={tfsService.getSessionTokenUri()} target="_blank">
                                            {Res.PersonalAccessToken}
                                        </Link>
                                        <em>{Res.Packaging}</em>
                                    </FormatComponent>
                                </div>

                                <div>
                                    <div className="title-xs">{Res.Step} 2</div>
                                    <FormatComponent format={Res.PastePATAndUserName_DotNet_Nuget}>
                                        <code>[{Res.PersonalAccessToken}]</code>
                                    </FormatComponent>
                                    <CopyableCode ariaLabel={Res.UpdateSource}>
                                        nuget sources update -ValidAuthenticationTypes basic -Name {source} -UserName "
                                        {source}" -Password [{Res.PersonalAccessToken}]
                                    </CopyableCode>
                                </div>
                            </div>
                        </React.Fragment>
                    )}
                </DetailsCard>

                <DetailsCard title={{ text: Res.RestorePackages, ariaLevel: 3 }}>
                    <p>{Res.RunCommand_Directory}</p>
                    <CopyableCode ariaLabel={Res.RestorePackages}>nuget.exe restore</CopyableCode>
                </DetailsCard>

                <PublishDetailsCard feed={feed}>
                    <p>{Res.PublishDetails_Nuget}</p>
                    <CopyableCode ariaLabel={Res.PublishPackages}>
                        nuget.exe push -Source "{source}" -ApiKey az &lt;packagePath&gt;
                    </CopyableCode>
                </PublishDetailsCard>
            </React.Fragment>
        );
    } else {
        return (
            <React.Fragment>
                <DetailsCard title={{ text: Res.ProjectSetup, ariaLevel: 3 }}>
                    <p>
                        <FormatComponent format={Res.ProjectSetup_Nuget_Details}>
                            <code>nuget.config</code>
                            <code>.csproj</code>
                            <code>.sln</code>
                        </FormatComponent>
                    </p>
                    <CopyableCode text={nugetPackageSource(source, url)} ariaLabel={Res.ProjectSetup}>
                        {nugetConfig(source, url)}
                    </CopyableCode>
                </DetailsCard>

                <DetailsCard title={{ text: Res.RestorePackages, ariaLevel: 3 }}>
                    <p>{Res.RunCommand_Directory}</p>
                    <CopyableCode ariaLabel={Res.RestorePackages}>nuget.exe restore</CopyableCode>
                </DetailsCard>
            </React.Fragment>
        );
    }
};

ToolDetailsComponents[ToolId.VisualStudio] = ({ tool, packagingService, feed }) => {
    const name = feed.name;
    const url = tool.getFeedUrl(packagingService, feed);

    return (
        <React.Fragment>
            <DetailsCard title={{ text: Res.MachineSetup, ariaLevel: 3 }}>
                <p>{Res.MachineSetup_VisualStudio_Details}</p>
                <p>{Res.Name}</p>
                <CopyableCode ariaLabel={Res.MachineSetup + " " + Res.Name}>{name}</CopyableCode>
                <p>{Res.Source}</p>
                <CopyableCode ariaLabel={Res.MachineSetup + " " + Res.Source}>{url}</CopyableCode>
                <p className="body-s">{Res.MachineSetup_VisualStudio_Note}</p>
            </DetailsCard>

            <DetailsCard title={{ text: Res.RestorePackages, ariaLevel: 3 }}>
                <p>{Res.RestorePackages_VisualStudio_Details}</p>
                <p>{Res.ForExample}</p>
                <CopyableCode ariaLabel={Res.RestorePackages}>Install-Package NUnit</CopyableCode>
            </DetailsCard>
        </React.Fragment>
    );
};

ToolDetailsComponents[ToolId.npm] = ({
    tool,
    packagingService,
    tfsService,
    feed,
    userIsMember,
    collectionName,
    isHosted
}) => {
    const [selectedTabId, setSelectedTabId] = React.useState<string>(OperatingSystem.Windows);
    const npmTool = tool as NpmTool;

    const registry = npmTool.getFeedUrl(packagingService, feed);
    const nerfDart = npmTool.getNerfDart(packagingService, feed);

    const stripRegistry = true;
    const nerfDartShort = npmTool.getNerfDart(packagingService, feed, stripRegistry);

    if (userIsMember) {
        return (
            <React.Fragment>
                <DetailsCard title={{ text: Res.ProjectSetup, ariaLevel: 3 }}>
                    <TabBar
                        onSelectedTabChanged={setSelectedTabId}
                        selectedTabId={selectedTabId}
                        tabSize={TabSize.Compact}
                    >
                        <Tab name="Windows" id={OperatingSystem.Windows} iconProps={{ iconName: "WindowsLogo" }} />
                        <Tab name={Res.Other} id={OperatingSystem.Other} iconProps={{ iconName: "PC1" }} />
                    </TabBar>

                    <p className="tooldetails-smaller-margin">{Res.ProjectSetup_Npm_Details}</p>
                    <CopyableCode ariaLabel={Res.ProjectSetup}>
                        <pre>{`registry=${registry} 
                        
always-auth=true`}</pre>
                    </CopyableCode>

                    {selectedTabId === OperatingSystem.Windows && isHosted && (
                        <React.Fragment>
                            <p>{Res.ProjectSetup_Npm_Windows_Details}</p>
                            <CopyableCode ariaLabel={Res.ProjectSetup}>
                                {Res.NpmConnectControl_AuthHelperCommand}
                            </CopyableCode>
                            <p className="body-s">{Res.ProjectSetup_Npm_Windows_Note}</p>
                        </React.Fragment>
                    )}

                    {selectedTabId === OperatingSystem.Windows && (
                        <React.Fragment>
                            <p className="body-s">
                                <FormatComponent format={Res.PAT_Issues}>
                                    <Link
                                        underline
                                        className="body-s"
                                        onClick={() => setSelectedTabId(OperatingSystem.Other)}
                                    >
                                        {Res.PAT_Instructions}
                                    </Link>
                                </FormatComponent>
                            </p>
                        </React.Fragment>
                    )}

                    {selectedTabId === OperatingSystem.Other && (
                        <React.Fragment>
                            <div className="title-s margin-top-16">{Res.SetupCredentials}</div>
                            <div className="rhythm-vertical-20 card-steps margin-top-8">
                                <div>
                                    <div className="title-xs">{Res.Step} 1</div>
                                    <p>
                                        <FormatComponent format={Res.Npm_CopyToNpmrc}>
                                            <FWLink underline id="724423">
                                                {Res.UserNpmrc}
                                            </FWLink>
                                        </FormatComponent>
                                    </p>
                                    <CopyableCode ariaLabel={Res.ProjectSetup}>
                                        ; {Res.BeginAuthToken} <br />
                                        {nerfDart}:username={collectionName} <br />
                                        {nerfDart}:_password=[{Res.EncodedPAT}] <br />
                                        {nerfDart}:email={Res.NpmRequiresEmail}
                                        <br />
                                        {nerfDartShort}:username={collectionName} <br />
                                        {nerfDartShort}:_password=[{Res.EncodedPAT}] <br />
                                        {nerfDartShort}:email={Res.NpmRequiresEmail}
                                        <br />; {Res.EndAuthToken}
                                    </CopyableCode>
                                </div>

                                <div>
                                    <div className="title-xs">{Res.Step} 2</div>
                                    <p>
                                        <FormatComponent format={Res.GeneratePAT}>
                                            <Link underline href={tfsService.getSessionTokenUri()} target="_blank">
                                                {Res.PersonalAccessToken}
                                            </Link>
                                            <em>{Res.Packaging}</em>
                                        </FormatComponent>
                                    </p>
                                </div>

                                <div>
                                    <div className="title-xs">{Res.Step} 3</div>
                                    <p>{Res.Npm_BaseEncodePAT}</p>
                                    <p>{Res.Npm_HowToEncode_Preamble}</p>
                                    <ol type="1">
                                        <li>1. {Res.Npm_RunCommand}</li>
                                        <li className="base64-encode-instructions">
                                            <CopyableCode ariaLabel={Res.ProjectSetup}>
                                                {`node -e \"require('readline') .createInterface({input:process.stdin,output:process.stdout,historySize:0}) .question('PAT> ',p => { b64=Buffer.from(p.trim()).toString('base64');console.log(b64);process.exit(); })\"`}
                                            </CopyableCode>
                                        </li>
                                        <li>2. {Res.Npm_PastePAT}</li>
                                        <li>3. {Res.Npm_CopyEncodedValue}</li>
                                    </ol>
                                </div>

                                <div>
                                    <div className="title-xs">{Res.Step} 4</div>
                                    <p>
                                        <FormatComponent format={Res.Npm_UpdateUserNpmrc}>
                                            <em>{Res.Both}</em>
                                            <code>[{Res.EncodedPAT}]</code>
                                            <code>.npmrc</code>
                                            <em>{Res.Base64}</em>
                                        </FormatComponent>
                                    </p>
                                </div>
                            </div>
                        </React.Fragment>
                    )}
                </DetailsCard>

                <DetailsCard title={{ text: Res.RestorePackages, ariaLevel: 3 }}>
                    <p>{Res.RunCommand_Directory}</p>
                    <CopyableCode ariaLabel={Res.RestorePackages}>npm install</CopyableCode>
                </DetailsCard>

                <PublishDetailsCard feed={feed}>
                    <p>{Res.RunCommand_Directory}</p>
                    <CopyableCode ariaLabel={Res.PublishPackages}>npm publish</CopyableCode>
                </PublishDetailsCard>
            </React.Fragment>
        );
    } else {
        return (
            <React.Fragment>
                <DetailsCard title={{ text: Res.ProjectSetup, ariaLevel: 3 }}>
                    <p>
                        <FormatComponent format={Res.Npm_ScopedNpmrc}>
                            <code>.npmrc</code>
                            <code>package.json</code>
                        </FormatComponent>
                    </p>
                    <CopyableCode ariaLabel={Res.ProjectSetup}>@[YOUR_SCOPE]/registry={registry}</CopyableCode>
                </DetailsCard>

                <DetailsCard title={{ text: Res.RestorePackages, ariaLevel: 3 }}>
                    <p>{Res.RunCommand_Directory}</p>
                    <CopyableCode ariaLabel={Res.RestorePackages}>npm install</CopyableCode>
                </DetailsCard>
            </React.Fragment>
        );
    }
};

ToolDetailsComponents[ToolId.Maven] = ({ tool, packagingService, tfsService, feed, collectionName, userIsMember }) => {
    const url = tool.getFeedUrl(packagingService, feed);
    const serverId = feed.name;

    const pomText = `<repository>
  <id>${serverId}</id>
  <url>${url}</url>
  <releases>
    <enabled>true</enabled>
  </releases>
  <snapshots>
    <enabled>true</enabled>
  </snapshots>
</repository>`;

    const settingsXmlPreamble = `<settings xmlns="http://maven.apache.org/SETTINGS/1.0.0"
          xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
          xsi:schemaLocation="http://maven.apache.org/SETTINGS/1.0.0
                              https://maven.apache.org/xsd/settings-1.0.0.xsd">
  <servers>`;
    const settingsXmlServer = `    <server>
      <id>${serverId}</id>
      <username>${collectionName}</username>
      <password>[${Res.PersonalAccessToken_Symbol}]</password>
    </server>`;
    const settingsXmlEpilogue = `  </servers>
</settings>`;

    if (userIsMember) {
        return (
            <React.Fragment>
                <DetailsCard title={{ text: Res.ProjectSetup, ariaLevel: 3 }}>
                    <p>
                        <FormatComponent format={Res.ProjectSetup_Maven_Pom}>
                            <strong>{Res.Both}</strong>
                            <code>&lt;repositories&gt;</code>
                            <code>&lt;distributionManagement&gt;</code>
                        </FormatComponent>
                    </p>
                    <CopyableCode ariaLabel={Res.ProjectSetup}>
                        <pre>{pomText}</pre>
                    </CopyableCode>

                    <p>
                        <FormatComponent format={Res.ProjectSetup_MavenGradle_Settings}>
                            <code>settings.xml</code>
                            <code>{"${user.home}/.m2"}</code>
                        </FormatComponent>
                    </p>
                    <CopyableCode text={settingsXmlServer} ariaLabel={Res.ProjectSetup}>
                        <pre className="secondary-text">{settingsXmlPreamble}</pre>
                        <pre>{settingsXmlServer}</pre>
                        <pre className="secondary-text">{settingsXmlEpilogue}</pre>
                    </CopyableCode>

                    <p>
                        <FormatComponent format={Res.GeneratePAT_MavenGradle}>
                            <Link underline href={tfsService.getSessionTokenUri()} target="_blank">
                                {Res.PersonalAccessToken}
                            </Link>
                            <em>{Res.Packaging}</em>
                        </FormatComponent>
                    </p>
                </DetailsCard>

                <DetailsCard title={{ text: Res.RestorePackages, ariaLevel: 3 }}>
                    <p>{Res.RunCommand_Directory}</p>
                    <CopyableCode ariaLabel={Res.RestorePackages}>mvn install</CopyableCode>
                </DetailsCard>

                <PublishDetailsCard feed={feed}>
                    <p>{Res.RunCommand_Directory}</p>
                    <CopyableCode ariaLabel={Res.PublishPackages}>mvn deploy</CopyableCode>
                </PublishDetailsCard>
            </React.Fragment>
        );
    } else {
        return (
            <React.Fragment>
                <DetailsCard title={{ text: Res.ProjectSetup, ariaLevel: 3 }}>
                    <p>
                        <FormatComponent format={Res.ProjectSetup_Maven_PomReplaceRepos}>
                            <code>pom.xml</code>
                            <code>&lt;repositories&gt;</code>
                            <code>&lt;/dependencies&gt;</code>
                        </FormatComponent>
                    </p>
                    <CopyableCode ariaLabel={Res.ProjectSetup}>
                        <pre>{pomText}</pre>
                    </CopyableCode>
                </DetailsCard>

                <DetailsCard title={{ text: Res.RestorePackages, ariaLevel: 3 }}>
                    <p>{Res.RestorePackages_Maven_Details}</p>
                    <CopyableCode ariaLabel={Res.RestorePackages}>mvn install</CopyableCode>
                </DetailsCard>
            </React.Fragment>
        );
    }
};

ToolDetailsComponents[ToolId.Gradle] = ({ tool, packagingService, tfsService, feed, collectionName, userIsMember }) => {
    const url = tool.getFeedUrl(packagingService, feed);
    const serverId = feed.name;

    if (userIsMember) {
        const buildGradleRepositories = `maven {
    url '${url}'
    name '${serverId}'
    authentication {
        basic(BasicAuthentication)
    }
}`;
        const settingsXmlPreamble = `<settings xmlns="http://maven.apache.org/SETTINGS/1.0.0"
          xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
          xsi:schemaLocation="http://maven.apache.org/SETTINGS/1.0.0
                              https://maven.apache.org/xsd/settings-1.0.0.xsd">
  <servers>`;
        const settingsXmlServer = `    <server>
      <id>${serverId}</id>
      <username>${collectionName}</username>
      <password>[${Res.PersonalAccessToken_Symbol}]</password>
    </server>`;
        const settingsXmlEpilogue = `  </servers>
</settings>`;

        return (
            <React.Fragment>
                <DetailsCard title={{ text: Res.ProjectSetup, ariaLevel: 3 }}>
                    <p>
                        <FormatComponent format={Res.ProjectSetup_Gradle_UpdateBuildGradle}>
                            <code>build.gradle</code>
                            <strong>{Res.Both}</strong>
                            <code>repositories</code>
                            <code>publishing.repositories</code>
                        </FormatComponent>
                    </p>
                    <CopyableCode ariaLabel={Res.ProjectSetup}>
                        <pre>{buildGradleRepositories}</pre>
                    </CopyableCode>

                    <p>
                        <FormatComponent format={Res.ProjectSetup_MavenGradle_Settings}>
                            <code>settings.xml</code>
                            <code>{"${user.home}/.m2"}</code>
                        </FormatComponent>
                    </p>
                    <CopyableCode text={settingsXmlServer} ariaLabel={Res.ProjectSetup}>
                        <pre className="secondary-text">{settingsXmlPreamble}</pre>
                        <pre>{settingsXmlServer}</pre>
                        <pre className="secondary-text">{settingsXmlEpilogue}</pre>
                    </CopyableCode>

                    <p>
                        <FormatComponent format={Res.GeneratePAT_MavenGradle}>
                            <Link underline href={tfsService.getSessionTokenUri()} target="_blank">
                                {Res.PersonalAccessToken}
                            </Link>
                            <em>{Res.Packaging}</em>
                        </FormatComponent>
                    </p>
                </DetailsCard>

                <DetailsCard title={{ text: Res.RestorePackages, ariaLevel: 3 }}>
                    <p>{Res.RunCommand_Directory}</p>
                    <CopyableCode ariaLabel={Res.RestorePackages}>gradle build</CopyableCode>
                </DetailsCard>

                <PublishDetailsCard feed={feed}>
                    <p>{Res.RunCommand_Directory}</p>
                    <CopyableCode ariaLabel={Res.PublishPackages}>gradle publish</CopyableCode>
                </PublishDetailsCard>
            </React.Fragment>
        );
    } else {
        return (
            <React.Fragment>
                <DetailsCard title={{ text: Res.ProjectSetup, ariaLevel: 3 }}>
                    <p>
                        <FormatComponent format={Res.ProjectSetup_Gradle_AltDetails}>
                            <code>build.gradle</code>
                            <code>repositories</code>
                        </FormatComponent>
                    </p>
                    <CopyableCode ariaLabel={Res.ProjectSetup}>
                        <pre>{`maven {
    url '${url}'
}`}</pre>
                    </CopyableCode>
                </DetailsCard>

                <DetailsCard title={{ text: Res.RestorePackages, ariaLevel: 3 }}>
                    <p>{Res.RunCommand_Directory}</p>
                    <CopyableCode ariaLabel={Res.RestorePackages}>gradle build</CopyableCode>
                </DetailsCard>
            </React.Fragment>
        );
    }
};

ToolDetailsComponents[ToolId.pip] = ({ tool, packagingService, feed }) => {
    const url = tool.getFeedUrl(packagingService, feed);

    return (
        <React.Fragment>
            <DetailsCard title={{ text: Res.ProjectSetup, ariaLevel: 3 }}>
                <FormatComponent format={Res.ProjectSetup_CredProvider}>
                    <b>{Res.ArtifactsKeyring}</b>
                    <b>{Res.ArtifactsKeyring}</b>
                </FormatComponent>
                <p>
                    <FormatComponent format={Res.ProjectSetup_Pip_VirtualEnv}>
                        <Link
                            href="https://go.microsoft.com/fwlink/?linkid=2103878"
                            target="_blank"
                            rel="nofollow noreferrer noopener help"
                            underline
                        >
                            {Res.TheseInstructions}
                        </Link>
                    </FormatComponent>
                </p>

                <p>
                    <FormatComponent format={Res.Pip_Config}>
                        <code>pip.ini</code>
                        <code>pip.conf</code>
                    </FormatComponent>
                </p>
                <CopyableCode ariaLabel={Res.ProjectSetup}>
                    <pre>{`[global]
index-url=${url}`}</pre>
                </CopyableCode>
            </DetailsCard>

            <DetailsCard title={{ text: Res.InstallPackages, ariaLevel: 3 }}>
                <p>{Res.RunCommand_Directory}</p>
                <CopyableCode ariaLabel={Res.InstallPackages}>pip install</CopyableCode>
            </DetailsCard>
        </React.Fragment>
    );
};

ToolDetailsComponents[ToolId.twine] = ({ tool, packagingService, feed }) => {
    const url = tool.getFeedUrl(packagingService, feed);

    return (
        <React.Fragment>
            <DetailsCard title={{ text: Res.ProjectSetup, ariaLevel: 3 }}>
                <FormatComponent format={Res.ProjectSetup_CredProvider}>
                    <b>{Res.ArtifactsKeyring}</b>
                    <b>{Res.ArtifactsKeyring}</b>
                </FormatComponent>
                <p>{Res.ProjectSetup_Twine_AddPypirc}</p>
                <CopyableCode ariaLabel={Res.ProjectSetup}>
                    <pre>{`[distutils]
Index-servers =
  ${feed.name}

[${feed.name}]
Repository = ${url}`}</pre>
                </CopyableCode>

                <p>
                    <FormatComponent format={Res.ProjectSetup_Twine_Recommendation}>
                        <code>.pypirc</code>
                        <code>[pypi]</code>
                        <code>.pypirc</code>
                    </FormatComponent>
                </p>
            </DetailsCard>

            <PublishDetailsCard feed={feed}>
                <p>{Res.Twine_CreateDistribution}</p>
                <CopyableCode ariaLabel={Res.PublishPackages}>python setup.py sdist bdist_wheel</CopyableCode>
                <p>
                    {Res.Twine_Publish}
                    <br />
                    <span className="font-weight-semibold">{Res.Important}: </span>
                    <FormatComponent format={Res.Twine_Publish_Note}>
                        <code>-r&nbsp;{feed.name}</code>
                    </FormatComponent>
                </p>
                <CopyableCode ariaLabel={Res.PublishPackages}>{`twine upload -r ${feed.name} dist/*`}</CopyableCode>
            </PublishDetailsCard>
        </React.Fragment>
    );
};

ToolDetailsComponents[ToolId.UniversalPackages] = ({ feed, packagingService, tfsService, collectionName }) => {
    const orgUrl = packagingService.getUrl().replace(/([\W])pkgs\./, "$1");
    const projectArg = feed.project
        ? `
    --project="${feed.project.name}"`
        : "";
    const scopeArg = feed.project
        ? `
    --scope project`
        : "";
    const [tabId, setTabId] = React.useState<string>(OperatingSystem.Windows);

    return (
        <React.Fragment>
            {feed.project && (
                <MessageCard severity={MessageCardSeverity.Warning}>
                    <FormatComponent format={Res.UPack_AzureCLI}>
                        <FWLink underline id="2110647">
                            {Res.Prerequisites}
                        </FWLink>
                    </FormatComponent>
                </MessageCard>
            )}

            <DetailsCard title={{ text: Res.ProjectSetup, ariaLevel: 3 }}>
                <TabBar onSelectedTabChanged={setTabId} selectedTabId={tabId} tabSize={TabSize.Compact}>
                    <Tab name="Windows" id={OperatingSystem.Windows} iconProps={{ iconName: "WindowsLogo" }} />
                    <Tab name={Res.Other} id={OperatingSystem.Other} iconProps={{ iconName: "PC1" }} />
                </TabBar>

                {tabId === OperatingSystem.Windows ? (
                    <React.Fragment>
                        <p className="tooldetails-smaller-margin">{Res.UPack_AzLogin}</p>

                        <CopyableCode ariaLabel={Res.ProjectSetup}>az login</CopyableCode>

                        <p className="body-s">
                            <FormatComponent format={Res.PAT_Issues}>
                                <Link underline className="body-s" onClick={() => setTabId(OperatingSystem.Other)}>
                                    {Res.PAT_Instructions}
                                </Link>
                            </FormatComponent>
                        </p>
                    </React.Fragment>
                ) : (
                    <React.Fragment>
                        <p className="tooldetails-smaller-margin">
                            <FormatComponent format={Res.GeneratePAT}>
                                <Link underline href={tfsService.getSessionTokenUri()} target="_blank">
                                    {Res.PersonalAccessToken}
                                </Link>
                                <em>{Res.Packaging}</em>
                            </FormatComponent>
                        </p>
                        <p>{Res.UPack_ADO_Login}</p>
                        <CopyableCode ariaLabel={Res.ProjectSetup}>
                            az devops login --organization {orgUrl}
                        </CopyableCode>
                    </React.Fragment>
                )}
            </DetailsCard>

            <DetailsCard title={{ text: Res.DownloadPackages, ariaLevel: 3 }}>
                <p>{Res.UPack_Download}</p>
                <CopyableCode
                    ariaLabel={Res.DownloadPackages}
                    text={`az artifacts universal download --organization ${orgUrl}${projectArg}${scopeArg} --feed ${feed.name} --name ${Res.MyFirstPackage} --version 0.0.1 --path .`}
                >
                    <pre>
                        {`az artifacts universal download
    --organization ${orgUrl}${projectArg}${scopeArg}
    --feed ${feed.name}
    --name ${Res.MyFirstPackage}
    --version 0.0.1
    --path .`}
                    </pre>
                </CopyableCode>
            </DetailsCard>

            <PublishDetailsCard feed={feed}>
                <p>{Res.UPack_Publish}</p>
                <CopyableCode
                    ariaLabel={Res.PublishPackages}
                    text={`az artifacts universal publish --organization ${orgUrl}${projectArg}${scopeArg} --feed ${feed.name} --name ${Res.MyFirstPackage} --version 0.0.1 --description "${Res.Upack_Welcome}" --path .`}
                >
                    <pre>
                        {`az artifacts universal publish
    --organization ${orgUrl}${projectArg}${scopeArg}
    --feed ${feed.name}
    --name ${Res.MyFirstPackage}
    --version 0.0.1
    --description "${Res.Upack_Welcome}"
    --path .`}
                    </pre>
                </CopyableCode>
            </PublishDetailsCard>
        </React.Fragment>
    );
};

ToolDetailsComponents[ToolId.Ivy] = ({ tool, packagingService, feed }) => {
    const uri = new Uri(tool.getFeedUrl(packagingService, feed));

    return (
        <React.Fragment>
            <DetailsCard title={{ text: Res.ProjectSetup, ariaLevel: 3 }}>
                <p>
                    <FormatComponent format={Res.ProjectSetup_Ivy_Details}>
                        <code>ivysettings.xml</code>
                    </FormatComponent>
                </p>
                <CopyableCode ariaLabel={Res.ProjectSetup}>
                    <pre>{`<ivysettings>
  <property name="repo.host" value="${uri.host}" />
  <property name="repo.realm" value="${uri.scheme}://${uri.host}" />
  <property name="repo.path" value="${uri.path}" />
  <property name="repo.user" value="User Name" />
  <property name="repo.pass" value="[${Res.PersonalAccessToken_Symbol}]" />
  <property name="repo.pattern" value="[organisation]/[module]/[revision]" />
  <credentials host="\${repo.host}" realm="\${repo.realm}" username="\${repo.user}" passwd="\${repo.pass}" />
  <settings defaultResolver="vsts_${feed.name}_ivy_resolver" />
  <resolvers>
    <url name="vsts_${feed.name}_ivy_resolver">
      <ivy pattern="\${repo.realm}/\${repo.path}/\${repo.pattern}/ivy.xml?correlationId=\${build.id}" />
      <artifact pattern="\${repo.realm}/\${repo.path}/\${repo.pattern}(/[branch])/[conf]/[artifact]-[revision].[ext]?correlationId=\${build.id}" />
    </url>
  </resolvers>
</ivysettings>`}</pre>
                </CopyableCode>

                <p>
                    <FormatComponent format={Res.GeneratePAT_Ivy}>
                        <em>{Res.Packaging}</em>
                    </FormatComponent>
                </p>
            </DetailsCard>

            <DetailsCard title={{ text: Res.RepositoryUrl, ariaLevel: 3 }}>
                <CopyableCode ariaLabel={Res.RepositoryUrl}>{uri.absoluteUri}</CopyableCode>
            </DetailsCard>
        </React.Fragment>
    );
};
